<template>
  <base-card no-header class="width-100">
    <template #body>
      <el-row>
        <el-col :span="5">
          <h3>
            {{ form.configurationName }}
          </h3>
          <h3 class="body-1 cursor-pointer mt-1" @click="openVersionModal">
            {{ $t('currentVersion') + ': ' + configuration.version }}
          </h3>
          <h3 v-if="props.nextRunTimestamp" class="body-1 mt-1">
            {{ $t('nextRun') + ': ' + formatTimestamp(props.nextRunTimestamp) }}
          </h3>
        </el-col>
        <el-col :span="15">
          <el-form ref="formRef" :model="form" :rules="formRules">
            <el-form-item
              class="mb-0"
              :label="$t('outputFileNameWithoutExtension')"
              prop="outputFileName"
            >
              <el-tooltip
                :disabled="!showOutputNameMismatchWarning"
                placement="top"
                :content="$t('outputFileNameNotMatchingAnyFileConfig')"
              >
                <el-input
                  v-model="form.outputFileName"
                  size="small"
                  :class="{ 'error-input': showOutputNameMismatchWarning }"
                  @change="emitUpdate"
                />
              </el-tooltip>
            </el-form-item>
            <el-form-item
              :label="$t('apiConnectionName')"
              class="mb-0"
              prop="apiConnectionName"
            >
              <el-tooltip
                :disabled="
                  props.apiConnections.find(
                    c => Number(c.apiName) === Number(form.apiConnectionName)
                  )?.enabled
                "
                placement="top"
                :content="$t('disabledApiConnectionRunsAreBlocked')"
              >
                <el-select
                  v-model="form.apiConnectionName"
                  disabled
                  size="small"
                  filterable
                  @change="emitUpdate"
                >
                  <el-option
                    v-for="opt in apiNameOptions"
                    :key="opt.value"
                    :label="opt.label"
                    :value="opt.value"
                  />
                </el-select>
              </el-tooltip>
            </el-form-item>
            <el-form-item
              :label="$t('executionTrigger')"
              class="mb-0"
              prop="executionTrigger"
            >
              <el-select
                v-model="form.executionTrigger"
                disabled
                size="small"
                filterable
                @change="emitUpdate"
              >
                <el-option
                  v-for="item in Object.keys(ExecutionTrigger)"
                  :key="item"
                  :label="item"
                  :value="ExecutionTrigger[item]"
                />
              </el-select>
            </el-form-item>
            <el-row>
              <el-col :span="8">
                <el-form-item
                  :label="$t('numberOfRetries')"
                  prop="numberOfRetries"
                >
                  <el-input-number
                    v-model="form.numberOfRetries"
                    :min="0"
                    :max="20"
                    @change="emitUpdate"
                  />
                </el-form-item>
              </el-col>
              <el-col :span="8">
                <el-form-item
                  :label="$t('retryDelayMilliseconds')"
                  prop="retryDelay"
                >
                  <el-input-number
                    v-model="form.retryDelay"
                    :min="0"
                    :max="30000"
                    :step="100"
                    @change="emitUpdate"
                  />
                </el-form-item>
              </el-col>
            </el-row>
          </el-form>
          <div
            v-if="form.executionTrigger === ExecutionTrigger.Schedule"
            class="mt-2"
          >
            <el-divider>{{ $t('timerTriggerConditions') }}</el-divider>
            <div class="timer-trigger-row mt-2 mb-2">
              <span class="timer-label">{{ $t('thisReportWillRun') }}</span>
              <el-select
                v-model="form.timerConditions.repeatFrequency"
                size="small"
                class="frequency-select"
                @change="
                  newVal => {
                    handleFrequencyChange(newVal);
                  }
                "
              >
                <el-option
                  v-for="opt in repeatFrequencyOptions"
                  :key="opt.value"
                  :label="$t(opt.label)"
                  :value="opt.value"
                ></el-option>
              </el-select>
              <span class="timer-label">{{ $t('starting') }}</span>
              <el-date-picker
                v-model="form.timerConditions.startDate"
                type="date"
                :placeholder="$t('startDate')"
                size="small"
                class="start-date-picker"
                format="YYYY-MM-DD"
                value-format="YYYY-MM-DD"
                @change="emitUpdate"
              ></el-date-picker>
            </div>
            <el-row :gutter="20" class="mt-2 mb-2">
              <el-col :span="8">
                <el-form-item :label="$t('endingDate')">
                  <el-date-picker
                    v-model="form.timerConditions.endDate"
                    type="date"
                    :placeholder="$t('optionalEndingDate')"
                    size="small"
                    format="YYYY-MM-DD"
                    value-format="YYYY-MM-DD"
                    clearable
                    @change="emitUpdate"
                  ></el-date-picker>
                </el-form-item>
              </el-col>
              <el-col :span="8">
                <el-form-item
                  v-if="
                    form.timerConditions.repeatFrequency ===
                    RepeatFrequency.Daily
                  "
                  :label="$t('executionTime')"
                >
                  <el-select
                    v-model="form.timerConditions.executionTime"
                    filterable
                    size="small"
                    @change="emitUpdate"
                  >
                    <el-option
                      v-for="time in executionTimeOptions"
                      :key="time"
                      :label="time"
                      :value="time"
                    />
                  </el-select>
                </el-form-item>
              </el-col>
              <el-col :span="8">
                <el-form-item
                  v-if="
                    form.timerConditions.repeatFrequency ===
                    RepeatFrequency.Daily
                  "
                  :label="$t('timeZone')"
                >
                  <el-select
                    v-model="form.timerConditions.executionTimeZone"
                    filterable
                    size="small"
                    @change="emitUpdate"
                  >
                    <el-option
                      v-for="tz in timeZoneOptions"
                      :key="tz.value"
                      :label="tz.label"
                      :value="tz.value"
                    />
                  </el-select>
                </el-form-item>
              </el-col>
            </el-row>
            <div
              v-if="
                form.timerConditions.repeatFrequency === RepeatFrequency.Daily
              "
              class="d-flex justify-space-between align-items-center mt-2 mb-1"
            >
              <span>{{ $t('runOnTheseDays') }}:</span>
              <el-button
                class="ml-2"
                type="primary"
                size="small"
                @click="selectAllDays"
              >
                {{ $t('selectAllDays') }}
              </el-button>
            </div>
            <div
              v-if="
                form.timerConditions.repeatFrequency === RepeatFrequency.Daily
              "
              class="day-selector mt-1"
            >
              <div
                v-for="day in daysOfWeek"
                :key="day.value"
                :class="[
                  'day-circle',
                  { active: selectedDays.includes(day.value) }
                ]"
                @click="toggleDay(day.value)"
              >
                {{ day.label }}
              </div>
            </div>
            <div
              v-if="
                form.timerConditions.repeatFrequency ===
                  RepeatFrequency.Daily && selectedDays.length
              "
              class="mt-3"
            >
              <el-divider>{{ $t('requestBodySettings') }}</el-divider>
              <el-tabs v-model="activeDay" type="card">
                <el-tab-pane
                  v-for="dayValue in selectedDays"
                  :key="dayValue"
                  :label="getDayLabel(dayValue)"
                  :name="String(dayValue)"
                >
                  <div class="api-settings-pane">
                    <el-row v-if="selectedDays.length > 1" class="mb-2">
                      <el-col :span="24" style="text-align: right">
                        <el-dropdown
                          @command="
                            targetDayValue =>
                              copyConfigToDay(dayValue, targetDayValue)
                          "
                        >
                          <el-button type="primary" size="small">
                            {{ $t('copyConfigTo') }}
                            <el-icon class="el-icon--right">
                              <ArrowDown />
                            </el-icon>
                          </el-button>
                          <template #dropdown>
                            <el-dropdown-menu>
                              <el-dropdown-item command="all">
                                {{ $t('allDays') }}
                              </el-dropdown-item>
                              <el-dropdown-item
                                v-for="otherDay in selectedDays.filter(
                                  d => d !== dayValue
                                )"
                                :key="otherDay"
                                :command="otherDay"
                              >
                                {{ getDayLabel(otherDay) }}
                              </el-dropdown-item>
                            </el-dropdown-menu>
                          </template>
                        </el-dropdown>
                      </el-col>
                    </el-row>

                    <api-request-body-settings
                      :settings="daysRequestBodyTemplates[dayValue]"
                      @add-filter="
                        addFilter(daysRequestBodyTemplates[dayValue])
                      "
                      @delete-filter="
                        filterIndex =>
                          deleteFilter(
                            daysRequestBodyTemplates[dayValue],
                            filterIndex
                          )
                      "
                      @change="emitUpdate"
                      @update-field="
                        (field, value) =>
                          updateApiRequestBodyField(
                            daysRequestBodyTemplates[dayValue],
                            field,
                            value
                          )
                      "
                    />
                  </div>
                </el-tab-pane>
              </el-tabs>
            </div>

            <div
              v-if="
                form.timerConditions.repeatFrequency !== RepeatFrequency.Daily
              "
              class="mt-3"
            >
              <el-divider>{{ $t('requestBodySettings') }}</el-divider>
              <api-request-body-settings
                :settings="singleRequestBodyTemplate"
                @change="emitUpdate"
                @add-filter="addFilter(singleRequestBodyTemplate)"
                @delete-filter="
                  filterIndex =>
                    deleteFilter(singleRequestBodyTemplate, filterIndex)
                "
                @update-field="
                  (field, value) =>
                    updateApiRequestBodyField(
                      singleRequestBodyTemplate,
                      field,
                      value
                    )
                "
              />
            </div>
          </div>
        </el-col>
        <el-col :span="4" class="actions-col-wrapper d-flex">
          <div class="actions-col d-flex">
            <img
              v-if="!disableDeleteAndSave"
              :src="require(`@/assets/images/icons/new/delete.svg`)"
              height="20"
              width="20"
              class="action-icon cursor-pointer"
              @click="deleteApiFileConfiguration"
            />
            <el-button
              v-if="!disableDeleteAndSave"
              type="primary"
              size="large"
              @click="saveItem"
            >
              {{ $t('save') }}
            </el-button>
          </div>
        </el-col>
      </el-row>
    </template>
  </base-card>
  <gain-configuration-versions-modal
    v-model="showVersionModal"
    :configuration-versions="props.configurationHistory"
    @restore="handleRestore"
    @close="showVersionModal = false"
  />
</template>
<script lang="ts">
import BaseCard from '@/components/panels/BaseCard.vue';
import GainConfigurationVersionsModal from '@/views/admin/engagements/components/GainConfigurationVersionsModal.vue';
import ApiRequestBodySettings from './ApiRequestBodySettings.vue';
import { mustNotBeEmpty } from '@/utils/formValidators';
import { getTimeZoneOptions } from '@/utils/timezoneHelpers';
import { computed, onMounted, onUpdated, ref } from 'vue';
import { daysOfWeek } from '../models/EtradeApiModels';
import { useRequestBodyTemplates } from '../composables/useRequestBodyTemplates';
import {
  isDailyRepeatFrequencyValid,
  areOtherRepeatFrequenciesValid
} from '../utils/apiFileValidation';
import {
  ElButton,
  ElCol,
  ElDatePicker,
  ElDivider,
  ElDropdown,
  ElDropdownMenu,
  ElDropdownItem,
  ElForm,
  ElFormItem,
  ElIcon,
  ElInput,
  ElInputNumber,
  ElMessage,
  ElOption,
  ElRow,
  ElSelect,
  ElTabPane,
  ElTabs,
  ElTooltip
} from 'element-plus';
import { ArrowDown } from '@element-plus/icons-vue';
import {
  ApiName,
  DataType,
  ExecutionTrigger,
  ValidationFailureBehaviour,
  ApiFileConfiguration,
  DayOfWeek,
  RepeatFrequency,
  FileLogicType,
  GainImportConfiguration,
  ApiConnectionConfiguration,
  TimerTriggerConditions,
  ScheduleEndType
} from '@etp/etp-gain-bff-client/axios';
import i18n from '@/i18n';
import moment from 'moment';

export default {
  name: 'ApiFileConfigurationCard',
  components: {
    BaseCard,
    GainConfigurationVersionsModal,
    ApiRequestBodySettings,
    ArrowDown,
    ElButton,
    ElCol,
    ElDatePicker,
    ElDivider,
    ElDropdown,
    ElDropdownMenu,
    ElDropdownItem,
    ElForm,
    ElFormItem,
    ElIcon,
    ElInput,
    ElInputNumber,
    ElOption,
    ElRow,
    ElSelect,
    ElTabPane,
    ElTabs,
    ElTooltip
  },
  props: {
    configuration: {
      type: Object as () => ApiFileConfiguration,
      default: {} as ApiFileConfiguration,
      required: true
    },
    configurationHistory: {
      type: Array as () => Array<ApiFileConfiguration>,
      default: () => new Array<ApiFileConfiguration>()
    },
    disableDeleteAndSave: { type: Boolean, default: false },
    gainImportConfiguration: {
      type: Object as () => GainImportConfiguration,
      required: true
    },
    apiConnections: {
      type: Array as () => Array<ApiConnectionConfiguration>,
      default: () => new Array<ApiConnectionConfiguration>()
    },
    exitingOutputFileNames: {
      type: Array as () => Array<string>,
      default: () => new Array<string>()
    },
    nextRunTimestamp: {
      type: String,
      default: ''
    }
  },
  emits: ['delete', 'update', 'save', 'fetch-history'],
  setup(props, { emit }) {
    const timeZoneOptions = getTimeZoneOptions();
    const DEFAULT_EXECUTION_TIME = '12:30';
    const DEFAULT_TIMEZONE = 'America/Los_Angeles';

    const showVersionModal = ref(false);
    const formRef = ref<HTMLFormElement>();

    interface FormData {
      configurationName: string;
      outputFileName: string;
      apiConnectionName: ApiName;
      executionTrigger: ExecutionTrigger;
      numberOfRetries: number;
      retryDelay: number;
      timerConditions: {
        startDate: string;
        repeatFrequency: RepeatFrequency;
        enabledDays: DayOfWeek[];
        endDate: string;
        executionTime: string;
        executionTimeZone: string;
      };
    }

    const form = ref<FormData>({
      configurationName: '',
      outputFileName: '',
      apiConnectionName: ApiName.Etrade,
      executionTrigger: ExecutionTrigger.Schedule,
      numberOfRetries: 0,
      retryDelay: 0,
      timerConditions: {
        endDate: '' as string,
        startDate: '' as string,
        repeatFrequency: RepeatFrequency.Daily,
        enabledDays: [] as DayOfWeek[],
        executionTime: DEFAULT_EXECUTION_TIME as string,
        executionTimeZone: DEFAULT_TIMEZONE
      }
    });

    const emitUpdate = () => {
      if (form.value.outputFileName) {
        form.value.outputFileName = form.value.outputFileName.trim();
      }

      const requestBodyTemplatesArray = getTemplatesArray(
        form.value.timerConditions.repeatFrequency
      );

      emit('update', {
        configurationName: form.value.configurationName,
        outputFileName: form.value.outputFileName,
        apiName: Number(form.value.apiConnectionName),
        executionTrigger: form.value.executionTrigger,
        numberOfRetries: form.value.numberOfRetries,
        retryDelay: form.value.retryDelay,
        timerTriggerConditions: outputTimerTriggerConditions.value,
        apiSettings: JSON.stringify(requestBodyTemplatesArray)
      } as ApiFileConfiguration);
    };

    const {
      selectedDays,
      activeDay,
      daysRequestBodyTemplates,
      singleRequestBodyTemplate,
      getDayLabel,
      selectAllDays,
      toggleDay,
      copyConfigToDay,
      addFilter,
      deleteFilter,
      updateApiRequestBodyField,
      handleFrequencyChange,
      initializeTemplates,
      getTemplatesArray
    } = useRequestBodyTemplates(
      emitUpdate,
      computed(() => form.value.timerConditions)
    );

    const apiNameOptions = computed(() =>
      Object.entries(ApiName)
        .map(([key, value]) => ({ label: key, value }))
        .filter(entry =>
          props.apiConnections.map(c => c.apiName).includes(entry.value)
        )
    );

    const repeatFrequencyOptions = computed(() =>
      Object.entries(RepeatFrequency).map(([key, value]) => ({
        label: key,
        value: value
      }))
    );

    const executionTimeOptions = computed(() => {
      const times: string[] = [];
      for (let hour = 0; hour < 24; hour++) {
        times.push(`${String(hour).padStart(2, '0')}:00`);
        times.push(`${String(hour).padStart(2, '0')}:30`);
      }
      return times;
    });

    const formRules = computed(() => ({
      outputFileName: [
        { validator: mustNotBeEmpty, trigger: 'blur' },
        {
          validator: localMustNotBeInArray,
          trigger: 'blur'
        }
      ]
    }));

    const removeExtension = (filename: string): string => {
      const lastDotIndex = filename.lastIndexOf('.');
      return lastDotIndex > 0 ? filename.substring(0, lastDotIndex) : filename;
    };

    const showOutputNameMismatchWarning = computed(() => {
      return (
        props.gainImportConfiguration.fileLogicType ===
          FileLogicType.BooleanLogic &&
        !props.gainImportConfiguration.fileConfigurations?.some(fc =>
          fc.namePattern
            ? removeExtension(fc.namePattern) === form.value.outputFileName
            : false
        )
      );
    });

    const localMustNotBeInArray = (rule: any, value: string, callback: any) => {
      if (
        props.exitingOutputFileNames.some(
          el => el.toLocaleLowerCase() == value.toLocaleLowerCase()
        )
      ) {
        callback(
          new Error(
            i18n.global.t('outputFileNameMustBeUniqueAcrossAllApiFileConfigs')
          )
        );
      } else callback();
    };

    const outputTimerTriggerConditions = computed<TimerTriggerConditions>(
      () => {
        const t = form.value.timerConditions;
        const isDaily = t.repeatFrequency === RepeatFrequency.Daily;
        return {
          repeatFrequency: t.repeatFrequency,
          startDate: t.startDate,
          enabledDays: t.enabledDays,
          endDate: t.endDate || null,
          endType: t.endDate ? ScheduleEndType.OnDate : ScheduleEndType.Never,
          executionTime: isDaily ? t.executionTime : null,
          executionTimeZone: isDaily ? t.executionTimeZone : null
        };
      }
    );

    const setFormValue = () => {
      form.value = {
        configurationName: props.configuration.configurationName ?? '',
        outputFileName: props.configuration.outputFileName ?? '',
        apiConnectionName: props.configuration.apiName ?? ApiName.Etrade,
        executionTrigger:
          props.configuration.executionTrigger ?? ExecutionTrigger.Schedule,
        numberOfRetries: props.configuration.numberOfRetries ?? 0,
        retryDelay: props.configuration.retryDelay ?? 0,
        timerConditions: {
          endDate: props.configuration.timerTriggerConditions?.endDate ?? '',
          startDate:
            props.configuration.timerTriggerConditions?.startDate ?? '',
          repeatFrequency:
            props.configuration.timerTriggerConditions?.repeatFrequency ??
            RepeatFrequency.Daily,
          enabledDays:
            props.configuration.timerTriggerConditions?.enabledDays ?? [],
          executionTime:
            props.configuration.timerTriggerConditions?.executionTime ?? '',
          executionTimeZone:
            props.configuration.timerTriggerConditions?.executionTimeZone ??
            DEFAULT_TIMEZONE
        }
      };

      if (props.configuration.apiSettings) {
        try {
          const templates = JSON.parse(props.configuration.apiSettings);
          initializeTemplates(templates);
        } catch {
          // Do nothing
        }
      }
    };

    const saveItem = async () => {
      let formValid = false;
      await formRef.value?.validate((valid: boolean) => {
        formValid = valid;
      });

      if (!formValid) {
        return;
      }

      if (form.value.executionTrigger === ExecutionTrigger.Schedule) {
        if (!form.value.timerConditions.startDate) {
          ElMessage.error('Start date is required for scheduled execution');
          return;
        }

        if (form.value.timerConditions.endDate) {
          if (
            moment(form.value.timerConditions.endDate).isBefore(
              moment(form.value.timerConditions.startDate)
            )
          ) {
            ElMessage.error('End date cannot be before start date');
            return;
          }
        }
      }

      if (
        form.value.timerConditions.repeatFrequency === RepeatFrequency.Daily
      ) {
        if (
          !isDailyRepeatFrequencyValid(
            form.value.timerConditions,
            selectedDays.value,
            daysRequestBodyTemplates.value,
            getDayLabel
          )
        )
          return;
      } else {
        if (!areOtherRepeatFrequenciesValid(singleRequestBodyTemplate.value))
          return;
      }
      emit('save');
    };

    const deleteApiFileConfiguration = () => {
      emit('delete');
    };

    const openVersionModal = () => {
      emit('fetch-history');
      showVersionModal.value = true;
    };

    const handleRestore = (input: ApiFileConfiguration) => {
      emit('update', input);
      showVersionModal.value = false;
    };

    const formatTimestamp = (timestamp: string): string => {
      if (!timestamp) return '';
      return moment(timestamp).format('YYYY-MM-DD HH:mm');
    };

    onMounted(() => setFormValue());
    onUpdated(() => setFormValue());

    return {
      activeDay,
      apiNameOptions,
      daysRequestBodyTemplates,
      selectedDays,
      executionTimeOptions,
      repeatFrequencyOptions,
      DataType,
      deleteApiFileConfiguration,
      emitUpdate,
      FileLogicType,
      form,
      formRef,
      formRules,
      formatTimestamp,
      props,
      RepeatFrequency,
      ValidationFailureBehaviour,
      ExecutionTrigger,
      daysOfWeek,
      selectAllDays,
      toggleDay,
      getDayLabel,
      addFilter,
      deleteFilter,
      updateApiRequestBodyField,
      copyConfigToDay,
      singleRequestBodyTemplate,
      handleFrequencyChange,
      timeZoneOptions,
      showOutputNameMismatchWarning,
      openVersionModal,
      showVersionModal,
      handleRestore,
      saveItem
    };
  }
};
</script>
<style lang="scss" scoped>
.day-selector {
  display: flex;
  gap: 8px;
}

.day-circle {
  width: 45px;
  height: 45px;
  border: 2px solid #dcdfe6;
  background: white;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;

  &:hover {
    border-color: $etp-deloitte-blue-7;
  }

  &.active {
    background: $etp-deloitte-blue-7;
    color: white;
    border-color: $etp-deloitte-blue-7;
  }
}

.timer-label {
  display: flex;
  width: auto;
  align-items: center;
  font-size: 16px;
  color: #606266;
  white-space: nowrap;
}

.timer-trigger-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: nowrap;
}

.frequency-select {
  width: 180px;
  flex-shrink: 0;
}

.start-date-picker {
  width: 160px;
  flex-shrink: 0;
  height: 100%;
}

:deep(.el-tabs__nav) {
  display: flex;
  width: 100%;
  border: none !important;
}

:deep(.el-tabs__item) {
  flex: 1;
  text-align: center;
  transition: 0.5s;
  color: $etp-shade-2;
  border: none !important;
  border-radius: 0 !important;
  background: transparent !important;
  box-shadow: none !important;

  &:hover {
    color: $etp-deloitte-blue-7;
  }
}

:deep(.el-tabs__item.is-active) {
  color: $etp-deloitte-blue-7;
  font-weight: 500;
  border-bottom: 2px solid $etp-deloitte-blue-7 !important;
  background: transparent !important;
  box-shadow: none !important;
}

:deep(.el-tabs__active-bar) {
  display: none;
}

.actions-col-wrapper {
  align-self: stretch;
}

.actions-col {
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;
  width: 100%;
  min-height: 100%;
}

:deep(.el-form-item.mb-0.is-error) {
  margin-bottom: 18px !important;
}
</style>
